import { 
  Footprints, Rocket, Target, Medal, Sword, Crown, 
  Flame, Zap, Trophy, Star, TrendingUp, Award,
  CalendarCheck, CalendarCheck2, CalendarRange, Gem,
  Sunrise, Moon, RotateCcw, Shield,
  LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const iconMap: Record<string, LucideIcon> = {
  'footprints': Footprints,
  'rocket': Rocket,
  'target': Target,
  'medal': Medal,
  'sword': Sword,
  'crown': Crown,
  'flame': Flame,
  'zap': Zap,
  'trophy': Trophy,
  'star': Star,
  'trending-up': TrendingUp,
  'award': Award,
  'calendar-check': CalendarCheck,
  'calendar-check-2': CalendarCheck2,
  'calendar-range': CalendarRange,
  'gem': Gem,
  'sunrise': Sunrise,
  'moon': Moon,
  'rotate-ccw': RotateCcw,
  'shield': Shield
};

const categoryColors: Record<string, string> = {
  'milestone': 'from-info to-info',
  'milestones': 'from-info to-info',
  'streak': 'from-warning to-destructive',
  'streaks': 'from-warning to-destructive',
  'performance': 'from-success to-success',
  'consistency': 'from-chart-4 to-chart-4',
  'dedication': 'from-warning to-warning'
};

interface AchievementBadgeProps {
  name: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  earned?: boolean;
  earnedAt?: string;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  className?: string;
}

export function AchievementBadge({
  name,
  description,
  icon,
  category,
  points,
  earned = false,
  earnedAt,
  size = 'md',
  showTooltip = true,
  className
}: AchievementBadgeProps) {
  const IconComponent = iconMap[icon] || Award;
  
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-14 h-14',
    lg: 'w-20 h-20'
  };

  const iconSizes = {
    sm: 'h-5 w-5',
    md: 'h-7 w-7',
    lg: 'h-10 w-10'
  };

  const badge = (
    <div className={cn("relative group", className)}>
      <div className={cn(
        "rounded-full flex items-center justify-center transition-all duration-300",
        sizeClasses[size],
        earned 
          ? `bg-gradient-to-br ${categoryColors[category] || categoryColors.milestone} text-primary-foreground shadow-lg`
          : "bg-muted/50 text-muted-foreground/40"
      )}>
        <IconComponent className={cn(iconSizes[size], earned && "drop-shadow-md")} />
      </div>
      {earned && (
        <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
          ✓
        </div>
      )}
    </div>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px]">
          <div className="text-center">
            <p className="font-semibold">{name}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
            <p className="text-xs font-medium text-primary mt-1">{points} pts</p>
            {earnedAt && (
              <p className="text-xs text-muted-foreground mt-1">
                Earned {new Date(earnedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
